import { useCallback, useRef, type ReactElement } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { mintGuestSession } from '../api/guestSessionApi';
import {
  getGuestSessionToken,
  setGuestSessionToken,
} from '../utils/guestSessionStorage';

const getRecaptchaSiteKey = (): string | undefined =>
  import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const TOKEN_EXPIRY_LEEWAY_MS = 30_000;

const decodeBase64UrlJson = (value: string): unknown => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
};

const isGuestSessionTokenReusable = (token: string | null): boolean => {
  if (!token) return false;

  try {
    const [, payload] = token.split('.');
    if (!payload) return false;
    const decoded = decodeBase64UrlJson(payload);
    const exp = typeof decoded === 'object' && decoded !== null ? (decoded as { exp?: unknown }).exp : null;
    return typeof exp === 'number' && exp * 1000 > Date.now() + TOKEN_EXPIRY_LEEWAY_MS;
  } catch {
    return false;
  }
};

type UseRecaptchaGuestSessionResult = {
  /** Render this once in the component tree to host the invisible widget. */
  recaptchaElement: ReactElement | null;
  /**
   * Ensures a guest session token exists. Solves the invisible reCAPTCHA and
   * mints a token on first call; subsequent calls reuse the stored token. Safe
   * to await before any guest AI request. No-op when the visitor is logged in.
   */
  ensureGuestSession: (isLoggedIn: boolean) => Promise<void>;
};

/**
 * Reusable invisible-reCAPTCHA → guest-session-token gate.
 *
 * Verifies the visitor is human once per session (matchmaking/navigator), then
 * relies on the minted token + per-IP throttle for the rest of the session —
 * the v2 token is single-use and short-lived, so re-solving per request would
 * be poor UX (security hardening Step 6).
 */
export const useRecaptchaGuestSession = (): UseRecaptchaGuestSessionResult => {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const siteKey = getRecaptchaSiteKey();

  const ensureGuestSession = useCallback(
    async (isLoggedIn: boolean): Promise<void> => {
      if (isLoggedIn) return;
      if (isGuestSessionTokenReusable(getGuestSessionToken())) return;
      if (inFlightRef.current) return inFlightRef.current;

      const run = (async () => {
        if (!siteKey || !recaptchaRef.current) {
          throw new Error('reCAPTCHA is not configured. Please refresh and try again.');
        }

        const recaptchaToken = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset();
        if (!recaptchaToken) {
          throw new Error('reCAPTCHA verification failed. Please try again.');
        }

        const session = await mintGuestSession(recaptchaToken);
        setGuestSessionToken(session.token, session.sid);
      })();

      inFlightRef.current = run;
      try {
        await run;
      } finally {
        inFlightRef.current = null;
      }
    },
    [siteKey],
  );

  const recaptchaElement = siteKey ? (
    <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={siteKey} />
  ) : null;

  return { recaptchaElement, ensureGuestSession };
};
